import React, { createContext, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "components/ui/dialog";

interface ModalProps {
  show: boolean;
  onHide: () => void;
  children: React.ReactNode;
  "aria-labelledby"?: string;
  dialogClassName?: string | boolean;
  backdrop?: string | boolean;
}

interface ModalHeaderProps {
  closeButton?: boolean;
  children: React.ReactNode;
}

interface ModalTitleProps {
  id?: string;
  children: React.ReactNode;
}

interface ModalBodyProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

interface ModalFooterProps {
  children: React.ReactNode;
}

interface ModalDismissProps {
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}

// Create context to pass onHide function down to child components
const ModalContext = createContext<{
  onHide: () => void;
} | null>(null);

const Modal: React.FC<ModalProps> & {
  Header: React.FC<ModalHeaderProps>;
  Title: React.FC<ModalTitleProps>;
  Body: React.FC<ModalBodyProps>;
  Footer: React.FC<ModalFooterProps>;
  Dismiss: React.FC<ModalDismissProps>;
} = ({
  show,
  onHide,
  children,
  "aria-labelledby": ariaLabelledBy,
  dialogClassName,
  backdrop,
}) => {
  const dialogClassNameValue =
    typeof dialogClassName === "string" ? dialogClassName : undefined;
  const staticBackdrop = backdrop === "static";

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onHide();
    }
  };

  return (
    <Dialog open={show} onOpenChange={handleOpenChange}>
      <ModalContext.Provider value={{ onHide }}>
        <DialogContent
          aria-describedby={undefined}
          aria-labelledby={ariaLabelledBy}
          className={`w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] gap-0 p-0 sm:max-w-[600px] ${dialogClassNameValue ?? ""}`}
          onPointerDownOutside={
            staticBackdrop ? (event) => event.preventDefault() : undefined
          }
          showCloseButton={false}
        >
          {children}
        </DialogContent>
      </ModalContext.Provider>
    </Dialog>
  );
};

const ModalHeader: React.FC<ModalHeaderProps> = ({ closeButton, children }) => {
  const context = useContext(ModalContext);

  return (
    <div className="flex items-center justify-between gap-4 px-6 pt-6">
      <div className="min-w-0 flex-1">{children}</div>
      {closeButton && context && (
        <Button
          aria-label="close"
          className="-mr-2 shrink-0"
          onClick={context.onHide}
          size="icon-sm"
          type="button"
          variant="ghost"
        >
          <FontAwesomeIcon aria-hidden="true" icon="xmark" />
        </Button>
      )}
    </div>
  );
};

const ModalTitle: React.FC<ModalTitleProps> = ({ id, children }) => {
  return (
    <DialogTitle className="text-xl font-medium leading-6" id={id}>
      {children}
    </DialogTitle>
  );
};

const ModalBody: React.FC<ModalBodyProps> = ({ children, style }) => {
  return (
    <div className="px-6 py-5" style={style}>
      {children}
    </div>
  );
};

const ModalFooter: React.FC<ModalFooterProps> = ({ children }) => {
  return <div className="flex flex-wrap justify-end gap-2 px-6 pb-6">{children}</div>;
};

const ModalDismiss: React.FC<ModalDismissProps> = ({
  className,
  children,
  onClick,
  disabled,
}) => {
  const context = useContext(ModalContext);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (context) {
      context.onHide();
    }
  };

  return (
    <Button
      className={className}
      onClick={handleClick}
      type="button"
      disabled={disabled}
    >
      {children}
    </Button>
  );
};

Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Dismiss = ModalDismiss;

export default Modal;
